import React, { useState, useEffect } from 'react';
import { Plus, Users, Edit, Trash2 } from 'lucide-react';
import { Card, Button, Input, Select, Table, Modal, Badge, ConfirmDialog } from '../components/ui';
import { personalService } from '../services/api';
import { formatMoney, formatDate, getLocalDate } from '../utils/formatters';
import { CARGOS_PERSONAL } from '../utils/constants';
import toast from 'react-hot-toast';

const PersonalPage = () => {
  const [personal, setPersonal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    nombre: '', dni: '', cargo: 'PORTERO', telefono: '', email: '',
    fecha_ingreso: getLocalDate(), sueldo_base: '',
    hora_entrada: '08:00', hora_salida: '17:00'
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await personalService.getAll();
      setPersonal(response.data.data || []);
    } catch (error) {
      toast.error('Error al cargar personal');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selected) {
        await personalService.update(selected.id, form);
        toast.success('Personal actualizado');
      } else {
        await personalService.create(form);
        toast.success('Personal registrado');
      }
      setModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar');
    }
  };

  const handleEdit = (emp) => {
    setSelected(emp);
    setForm({
      nombre: emp.nombre, dni: emp.dni, cargo: emp.cargo, telefono: emp.telefono || '',
      email: emp.email || '', fecha_ingreso: emp.fecha_ingreso?.split('T')[0] || emp.fecha_ingreso,
      sueldo_base: emp.sueldo_base, hora_entrada: emp.hora_entrada || '08:00', hora_salida: emp.hora_salida || '17:00'
    });
    setModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await personalService.delete(selected.id);
      toast.success('Personal eliminado');
      setConfirmOpen(false);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setSelected(null);
    setForm({
      nombre: '', dni: '', cargo: 'PORTERO', telefono: '', email: '',
      fecha_ingreso: getLocalDate(), sueldo_base: '',
      hora_entrada: '08:00', hora_salida: '17:00'
    });
  };

  const totalSueldos = personal.reduce((sum, p) => sum + parseFloat(p.sueldo_base || 0), 0);

  const columns = [
    { header: 'Nombre', accessor: 'nombre' },
    { header: 'DNI', accessor: 'dni' },
    { header: 'Cargo', render: (row) => <Badge variant="info">{CARGOS_PERSONAL.find(c => c.value === row.cargo)?.label || row.cargo}</Badge> },
    { header: 'Ingreso', render: (row) => formatDate(row.fecha_ingreso) },
    { header: 'Sueldo', render: (row) => <span className="font-semibold text-emerald-400">{formatMoney(row.sueldo_base)}</span> },
    { header: 'Horario', render: (row) => `${row.hora_entrada || '-'} - ${row.hora_salida || '-'}` },
    { header: 'Acciones', render: (row) => (
      <div className="flex gap-2">
        <button onClick={() => handleEdit(row)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white"><Edit size={16} /></button>
        <button onClick={() => { setSelected(row); setConfirmOpen(true); }} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-rose-400"><Trash2 size={16} /></button>
      </div>
    )}
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Personal</h1>
          <p className="text-slate-400">Gestión de empleados del edificio</p>
        </div>
        <Button icon={Plus} onClick={() => { resetForm(); setModalOpen(true); }}>Nuevo Personal</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20"><Users className="text-blue-400" size={20} /></div>
            <div>
              <p className="text-sm text-slate-400">Empleados Activos</p>
              <p className="text-xl font-bold text-white">{personal.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-slate-400">Total Planilla</p>
          <p className="text-xl font-bold text-white">{formatMoney(totalSueldos)}</p>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <Table columns={columns} data={personal} loading={loading} />
      </Card>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={selected ? 'Editar Personal' : 'Nuevo Personal'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Nombre Completo" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} required />
            <Input label="DNI" value={form.dni} onChange={(e) => setForm({ ...form, dni: e.target.value })} required />
            <Select label="Cargo" value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} options={CARGOS_PERSONAL} required />
            <Input label="Fecha Ingreso" type="date" value={form.fecha_ingreso} onChange={(e) => setForm({ ...form, fecha_ingreso: e.target.value })} required />
            <Input label="Sueldo Base (S/)" type="number" value={form.sueldo_base} onChange={(e) => setForm({ ...form, sueldo_base: e.target.value })} required />
            <Input label="Teléfono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
            <Input label="Hora Entrada" type="time" value={form.hora_entrada} onChange={(e) => setForm({ ...form, hora_entrada: e.target.value })} />
            <Input label="Hora Salida" type="time" value={form.hora_salida} onChange={(e) => setForm({ ...form, hora_salida: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit">{selected ? 'Actualizar' : 'Guardar'}</Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete} title="Eliminar Personal" />
    </div>
  );
};

export default PersonalPage;
